import React, { useLayoutEffect, useMemo, useRef, useState } from 'react';
import { formatDay, niceMax, type SalesPoint } from '../../lib/analytics';

/**
 * Sales over time: this period as a line, the previous period of the same length behind it.
 *
 * One measure, one axis. The previous period is context, not a second category, so it wears
 * the de-emphasis grey rather than a second hue; the legend and the tooltip both name the two
 * lines, and the table underneath carries every value for anyone who cannot use the plot.
 */

type Metric = 'revenue' | 'orders';

interface Props {
  series: SalesPoint[];
  previous: SalesPoint[];
  metric: Metric;
  formatValue: (v: number) => string;
}

// orange-600: the brand orange one step darker, which is where it stays legible as a 2px line
// on the dark card (the 500 step is too light for the dark-surface lightness band).
const CURRENT = '#ea580c';
const PREVIOUS = '#9ca3af';
const HEIGHT = 260;
const PAD = { top: 16, right: 16, bottom: 28, left: 56 };

const SalesChart: React.FC<Props> = ({ series, previous, metric, formatValue }) => {
  const wrapRef = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(720);
  const [hover, setHover] = useState<number | null>(null);

  useLayoutEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const measure = () => el.clientWidth > 0 && setWidth(el.clientWidth);
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, []);

  const n = series.length;
  const plotW = Math.max(width - PAD.left - PAD.right, 10);
  const plotH = HEIGHT - PAD.top - PAD.bottom;

  const { max, ticks } = useMemo(() => {
    const top = niceMax(Math.max(0, ...series.map((p) => p[metric]), ...previous.map((p) => p[metric])));
    return { max: top, ticks: [0, 0.25, 0.5, 0.75, 1].map((f) => f * top) };
  }, [series, previous, metric]);

  const x = (i: number) => PAD.left + (n <= 1 ? plotW / 2 : (i / (n - 1)) * plotW);
  const y = (v: number) => PAD.top + plotH - (v / max) * plotH;
  const path = (points: SalesPoint[]) =>
    points.map((p, i) => `${i === 0 ? 'M' : 'L'}${x(i).toFixed(1)},${y(p[metric]).toFixed(1)}`).join(' ');

  // Roughly one date label per 80px, always including the last day.
  const labelEvery = Math.max(1, Math.ceil(n / Math.max(2, Math.floor(plotW / 80))));

  const onMove = (e: React.MouseEvent<SVGRectElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    if (!rect.width || n === 0) return;
    const ratio = Math.min(1, Math.max(0, (e.clientX - rect.left) / rect.width));
    setHover(Math.round(ratio * (n - 1)));
  };

  const active = hover !== null ? series[hover] : null;
  const activePrev = hover !== null ? previous[hover] : null;
  const tooltipLeft = hover !== null ? Math.min(Math.max(x(hover), 90), width - 90) : 0;

  return (
    <div>
      <div className="flex items-center gap-5 text-xs text-white/60 mb-2">
        <span className="flex items-center gap-2">
          <span className="inline-block w-4 h-0.5 rounded" style={{ background: CURRENT }} />
          This period
        </span>
        <span className="flex items-center gap-2">
          <span className="inline-block w-4 h-0.5 rounded" style={{ background: PREVIOUS }} />
          Previous period
        </span>
      </div>

      <div ref={wrapRef} className="relative">
        <svg
          width={width}
          height={HEIGHT}
          role="img"
          aria-label={`${metric === 'revenue' ? 'Sales' : 'Orders'} per day, this period against the previous one`}
        >
          {ticks.map((t) => (
            <g key={t}>
              <line x1={PAD.left} x2={width - PAD.right} y1={y(t)} y2={y(t)} stroke="rgba(255,255,255,0.07)" strokeWidth={1} />
              <text x={PAD.left - 8} y={y(t) + 4} textAnchor="end" fontSize={11} fill="rgba(255,255,255,0.45)">
                {formatValue(t)}
              </text>
            </g>
          ))}

          {series.map((p, i) =>
            i % labelEvery === 0 || i === n - 1 ? (
              // Skip a regular label that would collide with the always-present last one.
              i !== n - 1 && n - 1 - i < labelEvery / 2 ? null : (
                <text
                  key={p.date}
                  x={x(i)}
                  y={HEIGHT - 8}
                  textAnchor={i === 0 ? 'start' : i === n - 1 ? 'end' : 'middle'}
                  fontSize={11}
                  fill="rgba(255,255,255,0.45)"
                >
                  {formatDay(p.date)}
                </text>
              )
            ) : null,
          )}

          <path d={path(previous)} fill="none" stroke={PREVIOUS} strokeOpacity={0.55} strokeWidth={1.5} strokeLinejoin="round" />
          <path d={path(series)} fill="none" stroke={CURRENT} strokeWidth={2} strokeLinejoin="round" strokeLinecap="round" />

          {active && hover !== null && (
            <g pointerEvents="none">
              <line x1={x(hover)} x2={x(hover)} y1={PAD.top} y2={PAD.top + plotH} stroke="rgba(255,255,255,0.25)" strokeWidth={1} />
              {activePrev && <circle cx={x(hover)} cy={y(activePrev[metric])} r={4} fill={PREVIOUS} stroke="#141210" strokeWidth={2} />}
              <circle cx={x(hover)} cy={y(active[metric])} r={4.5} fill={CURRENT} stroke="#141210" strokeWidth={2} />
            </g>
          )}

          <rect
            x={PAD.left}
            y={PAD.top}
            width={plotW}
            height={plotH}
            fill="transparent"
            onMouseMove={onMove}
            onMouseLeave={() => setHover(null)}
          />
        </svg>

        {active && (
          <div
            className="absolute top-2 -translate-x-1/2 pointer-events-none rounded-lg bg-[#1a1612] border border-white/10 px-3 py-2 text-xs shadow-lg whitespace-nowrap"
            style={{ left: tooltipLeft }}
          >
            <div className="text-white/50 mb-1">{formatDay(active.date)}</div>
            <div className="flex items-center gap-2 text-white">
              <span className="inline-block w-2 h-2 rounded-full" style={{ background: CURRENT }} />
              <span className="font-semibold">{formatValue(active[metric])}</span>
            </div>
            {activePrev && (
              <div className="flex items-center gap-2 text-white/60 mt-0.5">
                <span className="inline-block w-2 h-2 rounded-full" style={{ background: PREVIOUS }} />
                {formatValue(activePrev[metric])}
                <span className="text-white/35">{formatDay(activePrev.date)}</span>
              </div>
            )}
          </div>
        )}
      </div>

      <details className="mt-3 text-xs text-white/50">
        <summary className="cursor-pointer hover:text-white/70 w-fit">Show as table</summary>
        <div className="mt-2 max-h-64 overflow-auto">
          <table className="w-full text-left">
            <thead className="text-white/40">
              <tr>
                <th className="py-1 pr-4 font-medium">Day</th>
                <th className="py-1 pr-4 font-medium">This period</th>
                <th className="py-1 font-medium">Previous period</th>
              </tr>
            </thead>
            <tbody>
              {series.map((p, i) => (
                <tr key={p.date} className="border-t border-white/[0.05]">
                  <td className="py-1 pr-4">{formatDay(p.date)}</td>
                  <td className="py-1 pr-4 text-white/80">{formatValue(p[metric])}</td>
                  <td className="py-1">{previous[i] ? formatValue(previous[i][metric]) : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </details>
    </div>
  );
};

export default SalesChart;
